import Dexie from "dexie";
import { debugLogTime } from "../utils/common";
import merge from "deepmerge";

const CURRENT_DB_VERSION = "1.0.1";

const initDB = async () => {
  debugLogTime("DBService.createDB", DBService.DB_NAME, "DBService");
  const db = new Dexie(DBService.DB_NAME);
  if (!(await Dexie.exists(db.name))) {
    db.version(1).stores({});
  }
  if (!db.isOpen()) {
    await db.open();
  }
  debugLogTime("DBService.createDB", "Opened DB", "DBService");
  DBService.db = db;
  return db;
};

const getData = async (tableName) => {
  debugLogTime("DBService.getData", tableName, "DBService");
  const result = await DBService.db.table(tableName).toArray();
  return result;
};

const getDataById = async (tableName, id) => {
  debugLogTime("DBService.getDataById", `${tableName} -> ${id}`, "DBService");
  const result = await DBService.db.table(tableName).get(id);
  return result;
};

/**
 * check version of specified DB and clean up if necessary
 *
 *  Note: this is use to make sure we keep DB consistent in case we have different users
 *        use the same machine and browser or the DB schemas have completely changed.
 *

 * @param {string} userId
 */
const verifyDBVersion = async (userId) => {
  // implement
  const db = new Dexie(DBService.DB_NAME);
  if (await Dexie.exists(db.name)) {
    if (!db.isOpen()) {
      await db.open();
    }

    // check if this is the correct version and user
    const userAndDBVersion = await db.table("db_user_and_version").toArray();

    if (userAndDBVersion.length === 0) {
      debugLogTime("Deleting DB, ancient DB version", "", "DBService");
      await Dexie.delete(DBService.DB_NAME);
    }
  } else {
    debugLogTime("No DB exists !", "", "DBService");
  }
};

const updateData = async (tableName, data) => {
  let loadedData = {};
  if (DBService.db.table(tableName)) {
    loadedData = await DBService.getData(tableName);
  }
  await DBService.db.table(tableName).put(merge(loadedData, data));
};

const setUpdatedTable = async (tableName, data) => {
  let loadedData = {};
  if (DBService.db.table(`${tableName}_updated`)) {
    loadedData = await DBService.getData(`${tableName}_updated`);
  }

  await DBService.db.table(`${tableName}_updated`).put(merge(loadedData[0], data));
};

const setUpdated = async (table, data) => {
  let loadedData = [{}];
  if (DBService.db.table(`${table.dexieName}_updated`)) {
    loadedData = await DBService.getData(`${table.dexieName}_updated`);
  }

  await DBService.db.table(`${table.dexieName}_updated`).put(merge(loadedData[0] || {}, data));
};

const putAllData = async (tableName, data) => {
  debugLogTime("DBService.putAllData", tableName, "DBService");

  if (data !== null) {
    // put adds or overwrites if already exists - good for updates
    await DBService.db.table(tableName).bulkPut(data);
  }
};

const putDataNoUpdate = async (table, data) => {
  debugLogTime("DBService.putData", table.dexieName, "DBService");

  if (data !== null) {
    // put adds or overwrites if already exists - good for updates
    await DBService.db.table(table.dexieName).bulkPut(data);
  }

  // delete report_cache contents
  if ((await DBService.db.table("report_cache").count()) > 0) {
    await deleteData("report_cache");
  }
};

const tableExists = (tableName) => {
  const current = DBService.getCurrentSchema(DBService.db);
  return !!current[tableName];
};

/**
 * Updated a IndexedDB table with data and set last updated and version value
 *
 * @param {string} tableName
 * @param {array} data
 */
const putData = async (table, data, error, Update) => {
  debugLogTime("DBService.putData", table.dexieName, "DBService");

  let tableSize = -1;

  // if table exists
  if (DBService.db.table(`${table.dexieName}_updated`)) {
    const loadedData = await DBService.getData(`${table.dexieName}_updated`);
    tableSize = loadedData && loadedData.length ? loadedData[0].size : -1;
  }

  if (data !== null) {
    // put adds or overwrites if already exists - good for updates
    await DBService.db.table(table.dexieName).bulkPut(data);
    const loadedSize = await DBService.db.table(table.dexieName).count();

    await DBService.setUpdated(table, {
      id: 0,
      updated: Date.now(),
      size: tableSize,
      loaded: loadedSize,
      version: table.version,
      ...(error && { error }),
    });
  }

  // delete report_cache table
  if ((await DBService.db.table("report_cache").count()) > 0) {
    await deleteData("report_cache");
  }
};

const putDataById = async (tableName, id, data) => {
  debugLogTime("DBService.putDataById", `${tableName} -> ${id} ${JSON.stringify(data, null, 2)}`, "DBService");

  if (data !== null) {
    // put adds or overwrites if already exists - good for updates
    await DBService.db.table(tableName).put({ id, data });
  }
};

const putSimpleData = async (tableName, data) => {
  debugLogTime("DBService.putSimleData", `${tableName} ->  ${JSON.stringify(data, null, 2)}`, "DBService");

  if (data !== null) {
    // put adds or overwrites if already exists - good for updates
    await DBService.db.table(tableName).put(data);
  }
};

/**
 * Deletes all object from table store
 *
 * @param {string} tableName
 */
const deleteData = async (tableName) => {
  debugLogTime("DBService.deleteData", tableName, "DBService");

  await DBService.db.table(tableName).clear();
};

const deleteDataById = async (tableName, id) => {
  debugLogTime("DBService.deleteDataById", `${tableName} -> ${id}`, "DBService");

  await DBService.db.table(tableName).delete(id);
};

/**
 * Retrieve current IndexedDB schema
 *
 * @param {object} db
 * @returns the current schema
 */
const getCurrentSchema = (db) => {
  debugLogTime("DBService.getCurrentSchema", db.name, "DBService");

  return db.tables.reduce((result, { name, schema }) => {
    result[name] = [schema.primKey.src, ...schema.indexes.map((idx) => idx.src)].join(",");
    return result;
  }, {});
};

/**
 * Changes the current DB schema with passsed in changes
 *
 * @param {object} db
 * @param {object} schemaChanges
 * @returns
 */
const changeSchema = async (db, schemaChanges) => {
  debugLogTime("DBService.changeSchema", schemaChanges, "DBService");

  db.close();
  const newDb = new Dexie(db.name);

  newDb.on("blocked", () => false); // Silence console warning of blocked event.

  // Workaround: If DB is empty from tables, it needs to be recreated
  if (db.tables.length === 0) {
    await db.delete();
    newDb.version(1).stores(schemaChanges);
    return await newDb.open();
  }

  // Extract current schema in dexie format:
  const currentSchema = getCurrentSchema(db);

  debugLogTime("DBService.changeSchema Version:", db.verno, "DBService");
  debugLogTime("DBService.changeSchema Current Schema:", currentSchema, "DBService");

  // Tell Dexie about current schema:
  newDb.version(db.verno).stores(currentSchema);

  // Tell Dexie about next schema:
  newDb.version(db.verno + 1).stores(schemaChanges);

  DBService.db = await newDb.open();

  // Upgrade it:
  return DBService.db;
};

/*
The MIT License (MIT)
Copyright (c) 2015 Jonathan Smith
Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:
The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.
THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/

const showIndexedDbSize = () => {
  var db;
  var storesizes = [];

  function openDatabase() {
    return new Promise(function (resolve, reject) {
      if (DBService.DB_NAME !== null) {
        var request = window.indexedDB.open(DBService.DB_NAME);
        request.onsuccess = function (event) {
          db = event.target.result;
          resolve(db.objectStoreNames);
        };
      }
    });
  }

  function getObjectStoreData(storename) {
    return new Promise(function (resolve, reject) {
      var trans = db.transaction(storename, IDBTransaction.READ_ONLY);
      var store = trans.objectStore(storename);
      var items = [];
      trans.oncomplete = function (evt) {
        var szBytes = toSize(items);
        var szMBytes = (szBytes / 1024 / 1024).toFixed(2);
        storesizes.push({
          [`"${DBService.DB_NAME} DB:" Store Name`]: storename,
          Items: items.length,
          Size: szMBytes + " MB (" + szBytes + " bytes)",
        });
        resolve();
      };
      var cursorRequest = store.openCursor();
      cursorRequest.onerror = function (error) {
        reject(error);
      };
      cursorRequest.onsuccess = function (evt) {
        var cursor = evt.target.result;
        if (cursor) {
          items.push(cursor.value);
          cursor.continue();
        }
      };
    });
  }

  function toSize(items) {
    var size = 0;
    for (var i = 0; i < items.length; i++) {
      var objectSize = JSON.stringify(items[i]).length;
      size += objectSize * 2;
    }
    return size;
  }

  openDatabase().then(function (stores) {
    var PromiseArray = [];
    for (var i = 0; i < stores.length; i++) {
      PromiseArray.push(getObjectStoreData(stores[i]));
    }
    // TODO: may want to add a totals row
    Promise.all(PromiseArray).then(function () {
      console.table(storesizes);
    });
  });
};

const ensureTableExists = async (tableName, schema, requiresUpdated) => {
  if (!DBService.tableExists(tableName)) {
    const newSchema = DBService.getCurrentSchema(DBService.db);
    newSchema[tableName] = schema;
    if (requiresUpdated) {
      newSchema[`${tableName}_updated`] = "id++";
    }
    await DBService.changeSchema(DBService.db, newSchema);
  }
};

// Singleton - IIFE (Immediately Invoked Function Expression) i
const DBService = (function () {
  debugLogTime("DBService", "created", "Service");

  return {
    db: null,
    CURRENT_DB_VERSION,
    DB_NAME: null,
    initDB,
    verifyDBVersion,
    putData,
    putDataNoUpdate,
    getData,
    deleteData,
    putDataById,
    getDataById,
    putSimpleData,
    putAllData,
    deleteDataById,
    changeSchema,
    getCurrentSchema,
    ensureTableExists,
    showIndexedDbSize,
    tableExists,
    setUpdated,
    setUpdatedTable,
    updateData,
  };
})();

export default DBService;
